


































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { QuizQuestions } from '@/types/QuizQuestion';

import BaseComponent from '@/author/components/BaseComponent.vue';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import { getModule } from 'vuex-module-decorators';
import Bookmark from '@/models/Bookmark';
import { shuffle } from '@/author/components/helper/Array';
import { AnswerType } from '@/author/components/Quiz/AnswerTypes';
import { KNOWLEDGE_CHECK_EVENTS, QUIZ_FOLDER_PATH } from '@/author/constants';
import KnowledgeCheckEvents from '@/author/components/KnowledgeCheckParts/KnowledgeCheckEvents.vue';
import { QuizPoolSource } from '@/author/components/Quiz/QuizPoolSourceType';
import { localize } from '@/filters/localize';
import Timer from 'easytimer.js';
import { BTBA, StartQuizPoolProperties } from '@/analytics/events/BTBA';

import TOC from '@/views/toc.json';
import TableOfContents from '@/types/TableOfContents';
import { Question } from '@/types/Slide';
import { ThemeConfModule } from '@/store/modules/theme';
import { formatPercent } from '@/author/components/helper/i18n';

const AppState = getModule(AppModule, store);
const ThemeConf = getModule(ThemeConfModule, store);

Vue.filter('localize', localize);

@Component
export default class QuizPool extends BaseComponent {
  @Prop({ default: 'dark' }) theme?: string;

  @Prop() src!: QuizQuestions;
  @Prop({ default: false }) folderSrc!: boolean | string;

  @Prop({ default: true }) random!: boolean;
  @Prop({ default: 0 }) quantity!: number;

  @Prop() quizInstructions!: string;
  @Prop() accessibilityQuizInstructions!: string;

  @Prop({ required: false }) correctFeedback!: string;
  @Prop({ required: false }) incorrectFeedback!: string;

  @Prop({ required: false }) accessibilityCorrectFeedback!: string;
  @Prop({ required: false }) accessibilityIncorrectFeedback!: string;
  @Prop({ required: false }) accessibilitySelectFeedback!: string;

  @Prop({ default: true }) scorable!: boolean;
  @Prop({ required: false }) instantFeedback!: boolean;

  @Prop({ default: 'center' }) align?: string;
  @Prop({ default: 'auto' }) layout?: string;
  @Prop({ default: 0 }) padding?: string;
  @Prop({ default: 'normal' }) size?: string;

  @Prop(String) hiddenTitle!: string;

  @Prop(String) positiveTitle!: string;
  @Prop(String) positiveSubtitle!: string;
  @Prop(String) positiveMessage!: string;
  @Prop(String) positiveImage!: string;
  @Prop(String) positiveImageAlt!: string;

  @Prop(String) negativeTitle!: string;
  @Prop(String) negativeSubtitle!: string;
  @Prop(String) negativeMessage!: string;
  @Prop(String) negativeImage!: string;
  @Prop(String) negativeImageAlt!: string;

  @Prop(String) redoQuizLabel!: string;
  @Prop(String) reviewTrainingLabel!: string;
  @Prop(String) nextLabel!: string;
  @Prop(String) resultsLabel!: string;
  @Prop(String) submitLabel!: string;
  @Prop(String) correctQuestionTitle!: string;
  @Prop(String) incorrectQuestionTitle!: string;

  @Prop(String) topBarBackgroundImage!: string;
  @Prop(String) topBarBackgroundColor!: string;
  @Prop({ default: 'text' }) topBarDesign!: string;
  @Prop({ default: false }) invertTopBarColor!: boolean;

  @Prop(String) feedbackBackground!: string;
  @Prop(String) feedbackSize!: string;
  @Prop(Number) localPassingScore!: number;

  @Prop({default:false}) noFeedback !: boolean;

  current: number = -1;
  questionsPool: QuizQuestions = [];

  showFinalFeedback: boolean = false;

  sessionTimer : Timer = new Timer();

  // Scores used when scorable is false
  temporaryScore: number = 0;
  temporaryTotalScore: number = 0;

  // Saves all answers for the quiz questions
  allResults: AnswerType[] = [];

  orderedQuestions: Array<Record<string, string>> = [];

  get scoreTextForAccessibility() :string{
    return `${this.current + 1} / ${this.getQuantity} ( ${this.$options?.filters?.localize('correct score:')} ${this.results.score} )`;
  }

  get topBarTheme() {
    return this.invertTopBarColor ? this.getBackgroundTheme() : '';
  }

  get topBarTextTheme() {
    return this.invertTopBarColor ? '' : this.getTheme();
  }

  get getTopBarBackgroundColor() {
    return this.invertTopBarColor ? '' : this.topBarBackgroundColor;
  }

  get getTopBarBackgroundTextColor() {
    return this.invertTopBarColor ? this.topBarBackgroundColor : '';
  }

  get getTopBarImage() {
    return `url(${this.topBarBackgroundImage})`;
  }

  isRight(at: AnswerType) {
    return at == AnswerType.right;
  }

  isWrong(at: AnswerType) {
    return at == AnswerType.wrong;
  }

  isNeutral(at: AnswerType) {
    return at == AnswerType.neutral;
  }

  resetResultsHistory() {
    this.allResults = [];
    for (let index = 0; index < this.getQuantity; index++) 
      this.allResults.push(AnswerType.neutral);
    
    this.allResults[0] = AnswerType.active;
  }

  get questionsFromFolder() {
    return this.folderSrc ? this.getQuestionsFromSource() : [];
  }

  get hasFinishedQuiz() {
    if (!this.hasPass()) return false;
    return (
      this.scorable &&
      AppState.getScore + AppState.getTotalScore > 0 &&
      AppState.getTotalScore === this.getQuantity
    );
  }

  get questionsSourceType(): QuizPoolSource {
    return this.questionsFromFolder.length > 0
      ? QuizPoolSource.Folder
      : QuizPoolSource.Json;
  }

  get questionsSource() {
    return this.questionsSourceType === QuizPoolSource.Folder
      ? this.questionsFromFolder
      : this.src;
  }

  get optionsFromJson() {
    return this.questionsSourceType === QuizPoolSource.Json
      ? this.currentQuestion.options
      : [];
  }

  get shouldOrderQuestions() :boolean{
    return this.folderSrc !== false && !this.random;
  }

  defineQuestionsOrder() {
    const questionsOrderMap: Record<string, string> = {};
    this.questionsPool.map(({id}, index) => {
      //access vue's global component registry via proto hierarchy
      const component = this.$options?.components?.[id as string];
      //get original filename
      const name = ((component as any).options?.__file || (component as any).options?.name) ?? `question-${index}`;
      questionsOrderMap[name] = String(id);
    });
    //sort alphabetically
    const questionsOrder :string[] = Object.keys(questionsOrderMap);
    questionsOrder.sort();

    this.orderedQuestions = questionsOrder.map(question => {
      return {
        id: questionsOrderMap[question]
      };
    });
  }

  created() {

    if (this.hasFinishedQuiz) {
      this.showFinalFeedback = true;
      if (!this.hasPass()) this.lockForward();
    }else
      // quiz questions are setup here. So amount of questions in the pool gets updated on page change
      this.setupQuizQuestions();
     

    KnowledgeCheckEvents.$on(
      KNOWLEDGE_CHECK_EVENTS.MOUNTED,
      this.adaptQuestionToFolderSource
    );

  }

  mounted() {
    // TODO: analyze what portions of this code should be in mounted only
    if (this.shouldOrderQuestions) 
      this.defineQuestionsOrder();
    if (this.hasFinishedQuiz) {
      this.showFinalFeedback = true;
      if (!this.hasPass()) this.lockForward();
    } else 
      this.setupQuizPool();
    

    KnowledgeCheckEvents.$on(
      KNOWLEDGE_CHECK_EVENTS.MOUNTED,
      this.adaptQuestionToFolderSource
    );
    this.resetResultsHistory();
    // Called when a quiz shows a feedback
    KnowledgeCheckEvents.$on(
      KNOWLEDGE_CHECK_EVENTS.SHOWING_FEEDBACK,
      (answeredRight: boolean) => {
        this.handleQuestionFeedback(answeredRight);
      }
    );

    // Called after a quiz is answered and the user click on next button
    KnowledgeCheckEvents.$on(KNOWLEDGE_CHECK_EVENTS.NEXT, this.handleNext);

    // Called after the last quiz is answered and the user click on see result button
    KnowledgeCheckEvents.$on(
      KNOWLEDGE_CHECK_EVENTS.SHOW_FINAL_FEEDBACK,
      this.handleFinalFeedback
    );

    // ABOUT THE SCORE
    // Each quiz set the score directly in the store using the chooseAnswer method that is inside the BaseComponent
    // The quiz pool does NOT set the quantity of questions to the store. The quantitiy is defined by the amount of chooseAnswer executed (see the App Module)

    this.$analytics.trackEvent(BTBA.startQuizPool,{
      'Page ID': AppState.getCurrentSlide,
      'Page Title': AppState.getCurrentSlideTitle,
      'Pool Size' : this.getQuantity,
    } as StartQuizPoolProperties);
    this.sessionTimer.start();
  }

  beforeDestroy() {
    KnowledgeCheckEvents.$off([
      KNOWLEDGE_CHECK_EVENTS.SHOWING_FEEDBACK,
      KNOWLEDGE_CHECK_EVENTS.MOUNTED,
      KNOWLEDGE_CHECK_EVENTS.NEXT,
      KNOWLEDGE_CHECK_EVENTS.SHOW_FINAL_FEEDBACK
    ]);
  }

  setupQuizQuestions() { 
    // Shuffling everything if this.random is true.
    this.questionsPool = this.random
      ? shuffle([...this.questionsSource])
      : this.questionsSource;

    // Fallback sets 0 if order do not exist
    this.questionsPool = this.questionsPool.map(question => ({... question, order: question.order || 0 }));
    
    // Grouping question by order
    let poolGroups: any = this.questionsPool.reduce((prev,cur)=>{ 
      if(!Array.isArray(prev[cur.order]))
        prev[cur.order] = [];
      prev[cur.order].push(cur);
      return prev;
    },[] as any); 
     
    // Getting finalPool with questions from everygroup (respecting quantity)
    let finalPool : QuizQuestions = [];  
    let index = 0;
    let count = 0;
    let poolGroupsQtd = Object.keys(poolGroups).length; 
    while(count < this.getQuantity){
      let item = poolGroups[index].splice(0, 1)[0];
      if(item){
        finalPool.push(item);
        count++;
      }
      index++;
      if(index > poolGroupsQtd - 1)
        index = 0; 
      if(poolGroups.every((group:[]) => group.length == 0))
        break;
    }
 
    // Ordening by order number. this.random = true will resut in questions with the same order number to be random
    finalPool = finalPool.sort((a, b) => a.order - b.order);
    this.questionsPool = finalPool; 
  }

  setupQuizPool() {
    this.resetScore();
    this.setupQuizQuestions();
    if (this.isScorable) 
      AppState.resetScore();
    
    this.lockForward();
    this.adaptQuestionToFolderSource();
  }

  get getQuantity() {
    if (this.questionsPool.length == 0) return this.quantity;
    if (this.quantity == 0) return this.questionsPool.length;
    return Math.min(this.questionsPool.length, this.quantity);
  }

  adaptQuestionToFolderSource() {
    if (this.questionsSourceType == QuizPoolSource.Folder) {
      KnowledgeCheckEvents.$emit(
        KNOWLEDGE_CHECK_EVENTS.TURN_INTO_FOLDER_POOL,
        this.$props
      );

      KnowledgeCheckEvents.$emit(
        KNOWLEDGE_CHECK_EVENTS.IS_LAST_QUESTION,
        this.isLastQuestion
      );
    }
  }

  get currentQuestion() {
    if (this.shouldOrderQuestions)
      return this.orderedQuestions[this.current];
    return this.questionsPool[this.current];
  }

  resetScore() {
    this.current = 0;
    this.temporaryScore = 0;
    this.temporaryTotalScore = 0;
    this.showFinalFeedback = false;
    this.resetResultsHistory();
  }

  handleQuestionFeedback(result: boolean) {
    if (!this.isScorable) {
      this.temporaryTotalScore += 1;
      this.temporaryScore += result ? 1 : 0;
    }
    Vue.set(
      this.allResults,
      this.current,
      result ? AnswerType.right : AnswerType.wrong
    );
    
    if (!this.hasPass()) 
      this.lockForward();
    
  }

  handleNext() {
    this.current += 1;
    this.allResults[this.current] = AnswerType.active;
    this.focusOnSlideTitle();
    this.adaptQuestionToFolderSource();
  }

  get isLastQuestion() {
    return this.current >= this.getQuantity - 1;
  }

  handleFinalFeedback() {
    this.showFinalFeedback = true;
    if (this.scorable)
      this.setResults(AppState.getScore, AppState.getTotalScore);
    this.updateBookmark();
    if (this.hasPass()) 
      this.unlock();
    else 
      this.lockForward();
    
    this.$analytics.trackEvent(BTBA.finishedQuizPool,{
      'Page ID': AppState.getCurrentSlide,
      'Page Title': AppState.getCurrentSlideTitle,
      'Final Score' : AppState.getScore,
      'Minimum Score' : 0,
      'Maximum Score' : AppState.getTotalScore,
      'Scaled Score' : AppState.getScaledScore,
      'Pool Size' : this.getQuantity,
      'Duration':this.sessionTimer.getTimeValues().seconds
    } as StartQuizPoolProperties);
    this.sessionTimer.reset();
  }

  redoQuiz() {
    this.setupQuizPool();
  }

  reviewTraining() {
    AppState.goTo([AppState.tableOfContents.slides[0].id, 0]);
  }

  get scorePercentage() {
    return formatPercent(this.results.score / this.results.totalScore);
  }

  get passingScorePercentage() {
    let passingScore = ThemeConf.passingScore;
    if(!this.scorable && this.localPassingScore) passingScore = this.localPassingScore;
    return formatPercent(passingScore);
  }

  get isScorable(): boolean {
    if (this.hasPass()) return false;
    return this.scorable;
  }

  hasPass() {
    if (this.results.totalScore < this.getQuantity) return false;
    let passingScore = ThemeConf.passingScore;
    if(!this.scorable && this.localPassingScore) passingScore = this.localPassingScore;
    
    return (
      this.results.score / this.results.totalScore >= passingScore
    );
  }

  get results() {
    return this.scorable
      ? { score: AppState.getScore, totalScore: AppState.getTotalScore }
      : { score: this.temporaryScore, totalScore: this.temporaryTotalScore };
  }

  getTheme() {
    return {
      'color-light-text': this.theme == 'light',
      'color-dark-text': this.theme == 'dark',
      'color-primary': this.theme == 'primary'
    };
  }

  getBackgroundTheme() {
    return {
      'background-light': this.theme == 'light',
      'background-dark': this.theme == 'dark',
      'background-primary': this.theme == 'primary'
    };
  }

  parseScoredText(text: string) {
    return text
      .replace(/\{score\}/g, this.results.score.toString())
      .replace(/\{totalScore\}/g, this.results.totalScore.toString())
      .replace(/\{scorePercentage\}/g, this.scorePercentage.toString())
      .replace(/\{passingScorePercentage\}/g, this.passingScorePercentage.toString());
  }


  updateBookmark() {
    this.setBookmark(
      new Bookmark(
        AppState.getCurrentIndex,
        AppState.getScore,
        AppState.getTotalScore,
        AppState.getBiggestIndex,
        this.getBookmark().version
      )
    );
  }

  getQuestionsFromSource(): Question[] {
    const questionsPath = typeof this.folderSrc === 'string' ? this.folderSrc : QUIZ_FOLDER_PATH.ROOT;
    const TOCQuizes = (TOC as TableOfContents).quizes?.[questionsPath]?.questions;
    return (TOCQuizes ?? (TOC as TableOfContents).quiz?.questions) || [];
  }
}
